import { forwardRef } from 'react'
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp'
import { useLocalization } from '@/contexts/LocalizationContext'
import { normalizeDigits } from '@/utils/validation'
import { cn } from '@/lib/utils'

interface OTPInputProps {
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  error?: boolean
  'aria-label'?: string
  'aria-describedby'?: string
}

/**
 * Reusable OTP input component with 6-digit slots.
 *
 * - Localized label (falls back to English via the translation layer)
 * - Accepts Arabic-Indic / Persian numerals and normalizes them to 0-9
 * - Always renders left-to-right so the code order is stable under RTL
 * - Surfaces an error state on the slots (red + shake) in sync with the form
 */
export const OTPInput = forwardRef<HTMLInputElement, OTPInputProps>(
  (
    {
      value,
      onChange,
      disabled,
      error,
      'aria-label': ariaLabel,
      'aria-describedby': ariaDescribedBy,
    },
    ref,
  ) => {
    const { t } = useLocalization()

    const handleChange = (next: string) => {
      // Map Arabic-Indic/Persian numerals to Western digits and drop anything
      // that isn't a digit, so the value always satisfies validateOTP (^\d{6}$).
      onChange(normalizeDigits(next).replace(/\D/g, ''))
    }

    return (
      <div className="space-y-3 sm:space-y-4">
        <span
          id="otp-label"
          className="text-sm font-semibold text-gray-800 block text-center"
        >
          {t('auth.enterOtpCode')}
        </span>
        <div
          className="flex justify-center"
          role="group"
          aria-label={ariaLabel}
          aria-labelledby={ariaLabel ? undefined : 'otp-label'}
          aria-invalid={error || undefined}
        >
          <InputOTP
            ref={ref}
            dir="ltr"
            autoFocus
            maxLength={6}
            value={value}
            onChange={handleChange}
            disabled={disabled}
            aria-describedby={ariaDescribedBy}
          >
            {/* key toggles with `error` so the shake animation re-fires when an error appears */}
            <InputOTPGroup
              key={error ? 'otp-error' : 'otp-ok'}
              className={cn('gap-2 sm:gap-3', error && 'animate-shake')}
            >
              {Array.from({ length: 6 }).map((_, index) => (
                <InputOTPSlot
                  key={index}
                  index={index}
                  className={cn(
                    'w-11 h-11 text-lg sm:w-[3.25rem] sm:h-[3.25rem] sm:text-2xl font-bold border-2 rounded-lg sm:rounded-xl transition-all duration-200 bg-white',
                    error
                      ? 'border-red-400 ring-2 ring-red-200 text-red-700 data-[active=true]:border-red-500 data-[active=true]:ring-red-300'
                      : 'border-gray-200 hover:border-primary/50 hover:shadow-md focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20 focus-within:shadow-lg data-[active=true]:border-primary data-[active=true]:ring-2 data-[active=true]:ring-primary/20 data-[active=true]:shadow-lg',
                  )}
                />
              ))}
            </InputOTPGroup>
          </InputOTP>
        </div>
      </div>
    )
  },
)

OTPInput.displayName = 'OTPInput'
